import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { Product, ProductNotifications, RidangoReduxAction } from '@ridango/core';

import { PurchaseActions } from './purchase.actions';
import { PurchasePartialStoreState } from './purchase.reducer';
import * as PurchaseSelectors from './purchase.selectors';
import { TravelCardType } from '../../medium';

/**
 * This facade is to be used in purchase flows
 * for keeping user choices up until payloads are posted to server
 * to create tickets.
 *
 * @export
 * @class PurchaseFacade
 */
@Injectable()
export class PurchaseFacade {
    purchase$ = this.store.select(PurchaseSelectors.getPurchaseState);

    purchaseSteps$ = this.store.select(PurchaseSelectors.getPurchaseSteps);
    activePurchaseStep$ = this.store.select(PurchaseSelectors.getActivePurchaseStep);
    isFlowComplete$ = this.store.select(PurchaseSelectors.getFlowCompleted);
    payloads$ = this.store.select(PurchaseSelectors.getPayloads);
    invoiceLoading$ = this.store.select(PurchaseSelectors.getInvoiceLoading);
    notificationsFormValidity$ = this.store.select(PurchaseSelectors.getNotificationsFormValidity);
    categoryProductDetails$ = this.store.select(PurchaseSelectors.getCategoryProductDetails);

    constructor(
        @Inject('ENV') private env: any,
        private store: NgRedux<PurchasePartialStoreState>,
        private actions: PurchaseActions
    ) {}

    setChannel(channel: string = this.env.CHANNEL) {
        this.dispatch(this.actions.setChannel(channel));
    }

    dispatch(action: RidangoReduxAction) {
        this.store.dispatch(action);
    }

    fetchCategoryProductDetails() {
        this.dispatch({ type: PurchaseActions.FETCH_CATEGORY_PRODUCT_DETAILS });
    }

    setWalletPurchaseSteps(steps: any[]) {
        this.dispatch(this.actions.setWalletPurchaseSteps(steps));
    }

    setActiveWalletPurchaseStep(step: number) {
        this.dispatch(this.actions.setActiveWalletPurchaseStep(step));
    }

    prepareCorporatePayloads(payload) {
        this.dispatch(this.actions.prepareCorporatePayloads(payload));
    }

    setSelectedProduct(product: Product) {
        this.dispatch(this.actions.setWalletSelectedProduct(product));
    }

    setSelectedProductQty(payload: Product[]) {
        this.dispatch(this.actions.setWalletSelectedProductQuantity(payload));
    }

    setProductNotification(notifications: ProductNotifications) {
        this.dispatch(this.actions.setProductNotifications(notifications));
    }

    resetProductNotification() {
        this.dispatch(this.actions.ResetProductNotifications());
    }

    setProductNotificationFormValidity(status: boolean) {
        this.dispatch(this.actions.setProductNotificationFormValidity(status));
    }

    setWalletProductSum(sum: number) {
        this.dispatch(this.actions.setWalletSelectedSum(sum));
    }

    removeWalletProductSum() {
        this.dispatch(this.actions.removeWalletSelectedSum());
    }

    setFlowStatus(isComplete: boolean) {
        this.dispatch(this.actions.setWalletIsFlowCompleted(isComplete));
    }

    setWalletSelectedMedium(medium) {
        if (medium.cardType === TravelCardType.TravelCardU) {
            medium = { ...medium, cardType: null };
        }
        this.dispatch(this.actions.setWalletSelectedMedium(medium));
    }

    setPurchaseShoppingCartId(shopping_cart_id: number) {
        this.dispatch(this.actions.setPurchaseShoppingCartId(shopping_cart_id));
    }

    /**
     * Clears selected items/payloads. Keeps shopping cart id.
     *
     * Is used during purchase lifecycle: allows user to add more different tickets to existing cart.
     * @memberof PurchaseFacade
     */
    clearWalletPurchaseSelections() {
        this.dispatch(this.actions.clearWalletPurchaseSelections());
    }

    /**
     * Sets the purchase state to initial,
     * should be used when shopping cart gets cleared / purchase paid etc
     * @memberof PurchaseFacade
     */
    resetWalletPurchase() {
        this.dispatch(this.actions.resetWalletPurchase());
        this.setChannel(this.env.CHANNEL);
    }

    unsetShoppingCart() {
        this.dispatch(this.actions.unsetShoppingCartId());
    }

    unsetProduct() {
        this.dispatch(this.actions.unsetProduct());
    }

    unsetMedium() {
        this.dispatch(this.actions.unsetMedium());
    }

    setTicketPurchaseArea(area) {
        this.dispatch(this.actions.setTicketPurchaseArea(area));
    }

    setTicketPurchaseZones(originZoneId, destinationZoneId, originZoneName = '', destinationZoneName = '') {
        this.dispatch(
            this.actions.setTicketPurchaseZones(originZoneId, destinationZoneId, originZoneName, destinationZoneName)
        );
    }

    setTicketValidityDates(starting, ending) {
        this.dispatch(this.actions.setTicketValidityDates(starting, ending));
    }

    unsetTicketValidityDates() {
        this.dispatch(this.actions.unsetTicketValidityDates());
    }

    setRegionId(region_id: number) {
        this.dispatch(this.actions.setRegion(region_id));
    }

    setCartLanguage(language: string) {
        this.dispatch(this.actions.setCartLanguage(language));
    }

    setInvoiceLoading(loading: boolean) {
        this.dispatch(this.actions.setInvoiceLoading(loading));
    }

    removeCorporateTravelCard(travelcard_id: number) {
        this.dispatch(this.actions.removeCorporateTravelCard(travelcard_id));
    }

    getCategoryProductDetails() {
        return this.categoryProductDetails$;
    }
}
